/**
 * Badge Component
 * ===============
 * Covers all badge/chip/pill use-cases in the app:
 *  - CategoryBadge: book genre chip (Fantasy, Science…)
 *  - StatusBadge:   request status (pending, active, returned…)
 *  - DistanceBadge: book distance / "New" label
 *  - CountBadge:    notification dot with number
 *
 * Accessibility: badges are presentational.
 * Wrap in aria-label on the parent when the info matters to screen readers.
 */

import React from "react";
import { cn } from "@/theme/utils";
import { getCategoryStyle, getDistanceBadgeStyle } from "@/theme/utils";
import type { BookCategory, RequestStatus } from "@/types";

// ─── BASE BADGE ───────────────────────────────────────────────────────────────
interface BaseBadgeProps {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

const BaseBadge: React.FC<BaseBadgeProps> = ({ children, className, style }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1",
      "text-[9px] font-bold rounded-md px-1.5 py-0.5",
      "leading-none tracking-wide",
      className
    )}
    style={style}
  >
    {children}
  </span>
);

// ─── CATEGORY BADGE ───────────────────────────────────────────────────────────
interface CategoryBadgeProps {
  category: BookCategory;
  className?: string;
}

export const CategoryBadge: React.FC<CategoryBadgeProps> = ({
  category,
  className,
}) => {
  const { bg, text } = getCategoryStyle(category);
  const label = category.charAt(0).toUpperCase() + category.slice(1);

  return (
    <BaseBadge className={className} style={{ backgroundColor: bg, color: text }}>
      {label}
    </BaseBadge>
  );
};

// ─── DISTANCE BADGE ───────────────────────────────────────────────────────────
interface DistanceBadgeProps {
  distance: number;
  isNew?: boolean;
  className?: string;
}

export const DistanceBadge: React.FC<DistanceBadgeProps> = ({
  distance,
  isNew = false,
  className,
}) => {
  const { bg, text, label } = getDistanceBadgeStyle(distance, isNew);

  return (
    <BaseBadge className={className} style={{ backgroundColor: bg, color: text }}>
      {label}
    </BaseBadge>
  );
};

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
const statusConfig: Record<
  RequestStatus,
  { label: string; bg: string; text: string }
> = {
  pending:   { label: "Pending",   bg: "#FEF3C7", text: "#92400E" },
  approved:  { label: "Approved",  bg: "#D1FAE5", text: "#065F46" },
  active:    { label: "Active",    bg: "#EDE9FE", text: "#4C1D95" },
  returned:  { label: "Returned",  bg: "#F0EEF9", text: "#534AB7" },
  extended:  { label: "Extended",  bg: "#DBEAFE", text: "#1E3A8A" },
  cancelled: { label: "Cancelled", bg: "#F3F4F6", text: "#6B7280" },
  overdue:   { label: "Overdue ⚠", bg: "#FCEBEB", text: "#791F1F" },
};

interface StatusBadgeProps {
  status: RequestStatus;
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, className }) => {
  const config = statusConfig[status];

  return (
    <BaseBadge
      className={className}
      style={{ backgroundColor: config.bg, color: config.text }}
    >
      {config.label}
    </BaseBadge>
  );
};

// ─── COUNT BADGE (notification dot) ──────────────────────────────────────────
interface CountBadgeProps {
  count: number;
  /** Max number to show before showing "N+" */
  max?: number;
  className?: string;
}

export const CountBadge: React.FC<CountBadgeProps> = ({
  count,
  max = 9,
  className,
}) => {
  if (count <= 0) return null;
  const label = count > max ? `${max}+` : String(count);

  return (
    <span
      aria-label={`${count} notification${count !== 1 ? "s" : ""}`}
      className={cn(
        "absolute -top-1 -right-1",
        "flex items-center justify-center",
        "min-w-[16px] h-4 px-1",
        "text-[9px] font-bold text-white",
        "bg-error rounded-full border-2 border-white",
        className
      )}
    >
      {label}
    </span>
  );
};
